<template>
  <s-modal
    class="gas-volume-modal"
    v-model="showModal"
    cancel-confirm-message-code="MC006"
    :confirm-type="confirmationTypes.YES_NO"
    :ok-confirm-message-code="confirmMessageCode"
    :size="modalSize"
    :title="$t('engineering.water-on-paper.gas-volume-calculation')"
    :ok-only="isVisualizationMode"
    :ok-title="okTitle"
    :ok-disabled="isOkDisabled"
    :is-loading="loading"
    @ok="ok"
    @cancel="cancel"
    @show="onShowHandler"
  >
    <div class="gas-volume-modal__wrapper">
      <span class="gas-volume-modal__calculation">
        <span class="gas-volume-modal__numerator">
          <span class="gas-volume-modal__vg">
            VG (%) =
          </span>
          <span class="gas-volume-modal__tdgc">
            <em class="gas-volume-modal__tdgc-label">
              {{ $t('engineering.water-on-paper.tdgc') }}
            </em>
            <s-number-input
              class="gas-volume-modal__input"
              v-model="tdgc"
              :numeric-options="numericOptions"
              :validation="$v.tdgc"
              :invalid="$v.tdgc.$invalid"
              :disabled="isVisualizationMode"
              :key="tdgcKey"
            />
            <b-form-invalid-feedback class="gas-volume-modal__invalid-message">
              {{ $t('messages.MA086.message', invalidMessageParams) }}
            </b-form-invalid-feedback>
          </span>
          <span class="gas-volume-modal__multiplier">
            * 100
          </span>
        </span>
        <span class="gas-volume-modal__line-wrapper">
          <hr class="gas-volume-modal__line"/>
        </span>
        <span class="gas-volume-modal__denominator">
          1.000.000
        </span>
      </span>
      <span>
        <fixed-tooltip class="position-relative ml-2">
          <span class="d-flex text-left">
            {{ $t('messages.MI032.message') }}
          </span>
        </fixed-tooltip>
      </span>
    </div>
  </s-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';
import FixedTooltip from '@/components/FixedTooltip';
import Service from '@/domain/services/installation/engineering/waterOnPaper/waterOnPaperService';
import confirmationTypes from '@/constants/confirmationTypes';

const fieldRange = { min: 100, max: 1000000 };

export default {
  name: 'GasVolumeCalculationModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    isVisualizationMode: {
      type: Boolean,
      default: false,
    },
    gasVolume: {
      type: Number,
    },
  },
  components: {
    FixedTooltip,
  },
  data: () => ({
    tdgc: null,
    tdgcKey: 0,
    loading: false,
    confirmationTypes,
  }),
  validations: {
    tdgc: {
      required,
      minValue: minValue(fieldRange.min),
      maxValue: maxValue(fieldRange.max),
    },
  },
  computed: {
    ...mapGetters([
      'currentFontSize',
    ]),
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    modalSize() {
      return !this.currentFontSize.includes('14') ? 'lg' : 'md';
    },
    okTitle() {
      return this.isVisualizationMode ? this.$t('general.ok') : this.$t('general.calculate');
    },
    fieldState() {
      return this.tdgc === null ? null : this.$bsStateValidator(this.$v.tdgc.$invalid);
    },
    confirmMessageCode() {
      return this.isVisualizationMode ? null : 'MC011';
    },
    isOkDisabled() {
      return this.$v.$invalid && !this.isVisualizationMode;
    },
    invalidMessageParams() {
      return {
        minValue: fieldRange.min,
        maxValue: this.$n(fieldRange.max),
      };
    },
    numericOptions() {
      return {
        decimalPlaces: 0,
      };
    },
  },
  methods: {
    ok() {
      if (this.isVisualizationMode) {
        this.showModal = false;
        return null;
      }
      return this.calculate();
    },
    calculate() {
      this.loading = true;
      return Service.postGasesVolume({ tdgc: this.tdgc })
        .then((response) => {
          this.$emit('calculate', response);
          this.showModal = false;
          this.$pushAlert({ code: 'MS014' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onShowHandler() {
      this.loading = true;
      return Service.postTdgc({ gasVolume: this.gasVolume })
        .then(({ tdgc }) => {
          this.tdgc = tdgc;
          this.tdgcKey += 1;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancel() {
      this.tdgc = null;
      this.showModal = false;
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .gas-volume-modal
    &__wrapper
      display: flex
      justify-content: center
    &__vg
      font-weight: bold
      display: flex
      align-items: flex-start
      justify-content: flex-end
      white-space: nowrap
      margin-right: 5px
    &__calculation
      font-weight: bold
      display: flex
      flex-direction: column
      align-items: center
      white-space: nowrap
    &__numerator
      display: flex
      align-items: flex-end
    &__tdgc
      display: flex
      flex-direction: column
      align-items: center
    &__tdgc-label
      font-weight: normal
      color: $color-gray-medium-light
    &__multiplier
      display: flex
      align-items: center
      margin-left: 5px
    &__denominator
      display: flex
      justify-content: center
      width: 75%
      margin: 0 0 0 auto
    &__invalid-message
      width: 133px
      white-space: normal
      font-weight: normal
    &__line-wrapper
      display: flex
      justify-content: flex-end
      width: 100%
    &__line
      width: 75%
      margin: 8px 0 8px auto
    &__input
      width: 120px
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button
        -webkit-appearance: none
        margin: 0
</style>
